import React, { useEffect, useState } from "react";
import { PropsWithoutRef } from "react";

import {
  Drawer,
  Toolbar,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
} from "@mui/material";

import { useLocation, useNavigate } from "react-router-dom";

import Collapse from "@mui/material/Collapse";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { SIDEBAR_WIDTH } from "../constants";
import useAuth from "../hooks/useAuth";
import { USER_ROLES } from "../utils/enums";

interface Props {
  mobileOpen: boolean;
  handleSidebarToggle: () => void;
}

export default function Sidebar({
  mobileOpen,
  handleSidebarToggle,
}: PropsWithoutRef<Props>) {
  const user = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const sidebarItems = [
    {
      module: 1,
      label: "Dashboard",
      path: "/dashboard",
      icon: "/static/images/dashboard.png",
    },
    {
      module: 2,
      label: "Users",
      icon: "/static/images/user_list.png",
      collapseKey: "users",

      subItems: [
        {
          label: "Individuals",
          path: "/users/individual",
          collapseKey: "users",
          count: user?.count?.user || 0,
        },
        {
          label: "B2B",
          path: "/users/company",
          collapseKey: "users",
          count: user?.count?.companies || 0,
        },
        {
          label: "Wizard Team",
          path: "/users/agent",
          collapseKey: "users",
          count: user?.count?.salaried || 0,
        },
      ],
    },
    {
      module: 3,
      label: "Visa Type",
      path: "/visa-type",
      icon: "/static/images/visa.png",
    },
    {
      module: 4,
      label: "Applications",
      path: "/manage-applications",
      icon: "/static/images/form.png",
    },
    {
      module: 5,
      label: "Countries",
      path: "/manage-countries",
      icon: "/static/images/flag.png",
    },
    {
      module: 6,
      label: "Citizenship",
      path: "/manage-citizenship",
      icon: "/static/images/citizenship.png",
    },
    {
      module: 7,
      label: "Documents",
      path: "/manage-documents",
      icon: "/static/images/documents.png",
    },
    {
      module: 17,
      label: "Religions",
      path: "/manage-religions",
      icon: "/static/images/religion.png",
    },
    {
      module: 16,
      label: "Offer Letters",
      path: "/offer-letters",
      icon: "/static/images/letter.png",
    },
    // {
    //   module: 8,
    //   label: "Manage Branches",
    //   path: "/manage-branches",
    //   icon: "/static/images/branch.png",
    // },
    // {
    //   module: 9,
    //   label: "Manage Leaves",
    //   path: "/manage-leaves",
    //   icon: "/static/images/logout.png",
    // },
    {
      module: 10,
      label: "Salaries",
      path: "/manage-salaries",
      icon: "/static/images/salary.png",
    },
    {
      module: 11,
      label: "Payments",
      icon: "/static/images/income.png",
      collapseKey: "payments",
      subItems: [
        { label: "Payment Modes", path: "/manage-payments/payment-modes" },
        { label: "Payment Plans", path: "/manage-payments/payment-plans" },
        { label: "Payments", path: "/manage-payments/payments" },
      ],
    },
    {
      module: 0,
      label: "Sub-admin",
      icon: "/static/images/subadmin.png",
      collapseKey: "subAdmin",
      subItems: [
        { label: "Roles", path: "/user-management/roles" },
        { label: "Sub-admin List", path: "/user-management/user-list" },
      ],
    },
    {
      module: 12,
      label: "Reports and Analytics",
      path: "/reports",
      icon: "/static/images/data-analytics.png",
    },
    {
      module: 13,
      label: "Customer Support",
      path: "/customer-support",
      icon: "/static/images/customer-support.png",
    },
    {
      module: 14,
      label: "Notifications",
      path: "/manage-notifications",
      icon: "/static/images/notification.png",
    },
    {
      module: 15,
      label: "CMS",
      path: "/manage-cms",
      icon: "/static/images/cms.png",
    },
    {
      module: 18,
      label: "Settings",
      path: "/settings",
      icon: "/static/images/settings.png",
    },
  ];
  const [openMenus, setOpenMenus] = React.useState<{ [key: string]: boolean }>(
    {}
  );
  const [manuallyToggled, setManuallyToggled] = React.useState<Set<string>>(
    new Set()
  );

  const handleToggle = (key: string) => {
    setOpenMenus((prev) => {
      const newState = { ...prev, [key]: !prev[key] };

      if (newState[key]) {
        // Remove from manually toggled when opened
        setManuallyToggled((prevSet) => {
          const newSet = new Set(prevSet);
          newSet.delete(key);
          return newSet;
        });
      } else {
        // Add to manually toggled when closed
        setManuallyToggled((prevSet) => new Set(prevSet).add(key));
      }

      return newState;
    });
  };

  const filteredSidebarItems = sidebarItems
    ?.filter((item) => {
      if (!item?.module) return false; // Ensure item has a module before filtering

      // Find the permission for the module
      const permission = user?.role_permission?.find(
        (perm) => perm?.module_id === item?.module
      );

      return !!permission?.can_view; // Ensure boolean return value
    })
    .filter(Boolean);

  const [list, setList] = useState<any>(
    user?.user_role_type === USER_ROLES.ADMIN
      ? sidebarItems
      : filteredSidebarItems
  );

  React.useEffect(() => {
    setOpenMenus((prevOpenMenus) => {
      const newOpenMenus = { ...prevOpenMenus };

      sidebarItems?.forEach((item) => {
        if (item?.subItems) {
          const isSubItemActive = item?.subItems?.some((subItem) =>
            location?.pathname.startsWith(subItem?.path)
          );

          // Open only if a sub-item is active AND it hasn't been manually closed
          if (isSubItemActive && !manuallyToggled.has(item?.collapseKey)) {
            newOpenMenus[item?.collapseKey] = true;
          }
        }
      });

      return newOpenMenus;
    });
  }, [location.pathname, manuallyToggled]);

  useEffect(() => {
    if (user?.user_role_type === USER_ROLES.ADMIN) {
      setList(sidebarItems);
    } else if (user?.role_permission?.length) {
      const permission = user?.role_permission;

      const filteredItems = sidebarItems.filter((item) => {
        if (!item?.module) return false;

        const hasPermission = permission.some(
          (perm: any) => perm?.module_id === item?.module && perm?.can_view
        );

        return hasPermission;
      });

      setList(filteredItems);
    }
  }, [user]);

  const drawer = (
    <>
      <Toolbar className="sidebar-logo">
        <figure>
          <img
            src="/static/images/authLogo.png"
            alt=""
            // onClick={() => navigate("/dashboard")}
          />
        </figure>
      </Toolbar>

      <List sx={{ flexGrow: 1 }} className="sidebr-lst">
        {list?.map((item: any) => (
          <React.Fragment key={item?.module}>
            {/* Parent Menu Item */}
            <ListItemButton
              className="lst-itm"
              selected={
                location?.pathname?.startsWith(item?.path) ||
                (item?.subItems &&
                  item?.subItems?.some((subItem: { path: string }) =>
                    location?.pathname?.startsWith(subItem?.path)
                  ))
              }
              onClick={() =>
                item?.subItems
                  ? handleToggle(item?.collapseKey)
                  : navigate(item?.path)
              }
            >
              <ListItemIcon>
                <img src={item?.icon} alt="Icon" />
              </ListItemIcon>
              <ListItemText className="lstitm-txt" primary={item?.label} />
              {item?.subItems ? (
                openMenus[item?.collapseKey] ? (
                  <KeyboardArrowDownIcon />
                ) : (
                  <KeyboardArrowRightIcon />
                )
              ) : null}
            </ListItemButton>

            {/* Submenu Items */}
            {item?.subItems ? (
              <Collapse in={openMenus[item?.collapseKey]} timeout="auto">
                <List component="div" disablePadding>
                  {item?.subItems?.map((subItem: any) => (
                    <ListItemButton
                      key={subItem?.label}
                      selected={location?.pathname?.startsWith(subItem?.path)}
                      onClick={() => navigate(subItem?.path)} // Navigate without closing parent
                    >
                      <ListItemText primary={subItem?.label} />
                      {subItem?.count ? (
                        <div className="item_count">{subItem?.count || ""}</div>
                      ) : null}
                    </ListItemButton>
                  ))}
                </List>
              </Collapse>
            ) : null}
          </React.Fragment>
        ))}
      </List>
    </>
  );
  return (
    <Box
      component="nav"
      sx={{ flexShrink: { lg: 0 } }}
      className="sidebar-main-div"
    >
      <Drawer
        className="sidebar_drawer sidebar-main-div "
        variant="temporary"
        open={mobileOpen}
        onClose={handleSidebarToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", lg: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: SIDEBAR_WIDTH,
          },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        className="sidebar_drawer"
        sx={{
          display: { xs: "none", lg: "block" },
          "& .MuiDrawer-paper": {
            width: SIDEBAR_WIDTH,
            boxSizing: "border-box",
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
}
